import {
  Color,
  DoubleSide,
  Matrix4,
  MeshBasicMaterial,
  NoBlending,
  type Object3D,
  type PerspectiveCamera,
  RedFormat,
  RGBAFormat,
  type Scene,
  ShaderMaterial,
  SRGBColorSpace,
  UniformsUtils,
  Vector2,
  Vector3,
  type WebGLRenderer,
  WebGLRenderTarget,
} from '@seddi/three';
import { FullScreenQuad, Pass } from '@seddi/three/addons/postprocessing/Pass.js';
import { CopyShader } from '@seddi/three/addons/shaders/CopyShader.js';

export class OutlineSelectionPass extends Pass {
  readonly scene: Scene;
  camera: PerspectiveCamera;

  objects: Object3D[];
  renderToScreen: boolean = false;

  private static blurDirectionX: Vector2 = new Vector2(1.0, 0.0);
  private static blurDirectionY: Vector2 = new Vector2(0.0, 1.0);

  private readonly blur1stRenderTarget: WebGLRenderTarget;
  private readonly clearColor: Color;
  private readonly copyMaterial: ShaderMaterial;
  private readonly edgeDetectionMaterial: ShaderMaterial;
  private readonly edgeRenderTarget: WebGLRenderTarget;
  private readonly fsQuad: FullScreenQuad;
  private readonly maskBufferMaterial: MeshBasicMaterial;
  private readonly maskDownsampleRenderTarget: WebGLRenderTarget;
  private readonly maskRenderTarget: WebGLRenderTarget;
  private readonly overlayMaterial: ShaderMaterial;
  private readonly prepareMaskMaterial: ShaderMaterial;
  private readonly separableBlurMaterial1: ShaderMaterial;
  private readonly textureMatrix: Matrix4;
  private readonly visibleEdgeColor: Color;

  constructor(renderer: WebGLRenderer, scene: Scene, camera: PerspectiveCamera) {
    super();

    this.scene = scene;
    this.camera = camera;
    this.objects = [];

    // this.hiddenEdgeColor = this.visibleEdgeColor.clone();
    this.visibleEdgeColor = new Color(0x4e0fd2).convertSRGBToLinear();

    const resolution = renderer.getSize(new Vector2());
    const resx = Math.round(resolution.x / DOWNSAMPLE_RATIO);
    const resy = Math.round(resolution.y / DOWNSAMPLE_RATIO);
    this.maskBufferMaterial = new MeshBasicMaterial({ color: 0xffffff });
    this.maskBufferMaterial.side = DoubleSide;
    this.maskRenderTarget = new WebGLRenderTarget(resolution.x, resolution.y, {
      depthBuffer: false,
      format: RedFormat,
    });
    this.maskRenderTarget.texture.name = 'OutlinePass.mask';
    // this.depthMaterial = new MeshDepthMaterial();
    // this.depthMaterial.side = DoubleSide;
    // this.depthMaterial.depthPacking = RGBADepthPacking;
    // this.depthMaterial.blending = NoBlending;
    this.prepareMaskMaterial = createPrepareMaskMaterial();
    // this.depthRenderTarget = new WebGLRenderTarget(resolution.x, resolution.y, pars);
    // this.depthRenderTarget.texture.name = 'OutlinePass.depth';
    this.maskDownsampleRenderTarget = new WebGLRenderTarget(resx, resy, {
      depthBuffer: false,
      format: RedFormat,
    });
    this.maskDownsampleRenderTarget.texture.name = 'OutlinePass.depthDownSample';
    this.blur1stRenderTarget = new WebGLRenderTarget(resx, resy, {
      colorSpace: SRGBColorSpace,
      depthBuffer: false,
      format: RGBAFormat,
    });
    this.blur1stRenderTarget.texture.name = 'OutlinePass.blur1';
    // this.blur2ndRenderTarget = new WebGLRenderTarget(
    //   Math.round(resx / 2),
    //   Math.round(resy / 2),
    //   pars,
    // );
    // this.blur2ndRenderTarget.texture.name = 'OutlinePass.blur2';
    this.edgeDetectionMaterial = createEdgeDetectionMaterial();
    this.edgeRenderTarget = new WebGLRenderTarget(resx, resy, {
      colorSpace: SRGBColorSpace,
      depthBuffer: false,
      format: RGBAFormat,
    });
    this.edgeRenderTarget.texture.name = 'OutlinePass.edge1';
    // this.renderTargetEdgeBuffer2 = new WebGLRenderTarget(
    //   Math.round(resx / 2),
    //   Math.round(resy / 2),
    //   pars,
    // );
    // this.renderTargetEdgeBuffer2.texture.name = 'OutlinePass.edge2';
    const MAX_EDGE_THICKNESS = 4;
    this.separableBlurMaterial1 = createSeperableBlurMaterial(MAX_EDGE_THICKNESS);
    this.separableBlurMaterial1.uniforms.kernelRadius.value = 1;
    this.copyMaterial = createCopyMaterial();
    this.overlayMaterial = createOverlayMaterial();

    this.fsQuad = new FullScreenQuad(null);
    this.clearColor = new Color();
    this.textureMatrix = new Matrix4();
  }

  dispose() {
    this.maskRenderTarget.dispose();
    // this.depthRenderTarget.dispose();
    this.maskDownsampleRenderTarget.dispose();
    this.blur1stRenderTarget.dispose();
    this.edgeRenderTarget.dispose();
    // this.blur2ndRenderTarget.dispose();
    // this.renderTargetEdgeBuffer2.dispose();

    super.dispose();
  }

  setSize(width: number, height: number) {
    this.maskRenderTarget.setSize(width, height);
    // this.depthRenderTarget.setSize(width, height);
    let resx = Math.round(width / DOWNSAMPLE_RATIO);
    let resy = Math.round(height / DOWNSAMPLE_RATIO);
    this.maskDownsampleRenderTarget.setSize(resx, resy);
    this.blur1stRenderTarget.setSize(resx, resy);
    this.edgeRenderTarget.setSize(resx, resy);
    this.edgeDetectionMaterial.uniforms.texSize.value.set(resx, resy);
    this.separableBlurMaterial1.uniforms.texSize.value.set(resx, resy);
    // resx = Math.round(resx / 2);
    // resy = Math.round(resy / 2);
    // this.blur2ndRenderTarget.setSize(resx, resy);
    // this.renderTargetEdgeBuffer2.setSize(resx, resy);
    // this.separableBlurMaterial2.uniforms.texSize.value.set(resx, resy);
  }

  changeVisibilityOfSelectedObjects(bVisible: boolean) {
    const { objects } = this;
    objects.forEach((root) =>
      root.traverse((node) => {
        const mesh = node as any;
        if (mesh.isMesh || mesh.isLine || mesh.isLineSegments || mesh.isSprite) {
          if (bVisible) {
            mesh.visible = mesh.userData.oldVisible;
            delete mesh.userData.oldVisible;
          } else {
            mesh.userData.oldVisible = mesh.visible;
            mesh.visible = bVisible;
          }
        }
      }),
    );
  }

  changeVisibilityOfNonSelectedObjects(bVisible: boolean) {
    const { scene, objects } = this;
    const selectedMeshes = new Map();
    objects.forEach((root) =>
      root.traverse((node) => {
        const mesh = node as any;
        if (mesh.isMesh || mesh.isLine || mesh.isLineSegments || mesh.isSprite) {
          selectedMeshes.set(mesh.id, true);
        }
      }),
    );
    scene.traverse((node) => {
      const mesh = node as any;
      if (
        !selectedMeshes.has(node.id) &&
        (mesh.isMesh || mesh.isLine || mesh.isLineSegments || mesh.isSprite)
      ) {
        const visibility = mesh.visible;
        if (!bVisible || mesh.bVisible) {
          mesh.visible = bVisible;
        }
        mesh.bVisible = visibility;
      }
    });
  }

  updateTextureMatrix() {
    this.textureMatrix.set(
      0.5,
      0.0,
      0.0,
      0.5,
      0.0,
      0.5,
      0.0,
      0.5,
      0.0,
      0.0,
      0.5,
      0.5,
      0.0,
      0.0,
      0.0,
      1.0,
    );
    this.textureMatrix.multiply(this.camera.projectionMatrix);
    this.textureMatrix.multiply(this.camera.matrixWorldInverse);
  }

  render(renderer: WebGLRenderer, writeBuffer: WebGLRenderTarget, readBuffer: WebGLRenderTarget) {
    const {
      separableBlurMaterial1,
      camera,
      copyMaterial,
      // depthMaterial,
      edgeDetectionMaterial,
      fsQuad,
      // hiddenEdgeColor,
      clearColor,
      overlayMaterial,
      // patternTexture,
      prepareMaskMaterial,
      blur1stRenderTarget,
      // depthRenderTarget,
      edgeRenderTarget,
      maskRenderTarget,
      maskDownsampleRenderTarget,
      renderToScreen,
      scene,
      objects,
      textureMatrix,
      visibleEdgeColor,
    } = this;

    if (objects.length > 0) {
      renderer.getClearColor(clearColor);
      const clearAlpha = renderer.getClearAlpha();
      const autoClear = renderer.autoClear;
      renderer.autoClear = false;
      renderer.setClearColor(0xffffff, 1);

      // Make selected objects invisible
      // this.changeVisibilityOfSelectedObjects(false);
      const background = scene.background;
      scene.background = null;

      // 1. Draw Non Selected objects in the depth buffer
      // scene.overrideMaterial = depthMaterial;
      // renderer.setRenderTarget(depthRenderTarget);
      // renderer.clear();
      // renderer.render(scene, camera);

      // Make selected objects visible
      // this.changeVisibilityOfSelectedObjects(true);

      // Update Texture Matrix for Depth compare
      this.updateTextureMatrix();

      // Make non selected objects invisible, and draw only the selected objects, by comparing the depth buffer of non selected objects
      this.changeVisibilityOfNonSelectedObjects(false);
      scene.overrideMaterial = prepareMaskMaterial;
      prepareMaskMaterial.uniforms.cameraNearFar.value = new Vector2(camera.near, camera.far);
      // prepareMaskMaterial.uniforms.depthTexture.value = depthRenderTarget.texture;
      prepareMaskMaterial.uniforms.textureMatrix.value = textureMatrix;
      renderer.setRenderTarget(maskRenderTarget);
      renderer.clear();
      renderer.render(scene, camera);
      scene.overrideMaterial = null;
      this.changeVisibilityOfNonSelectedObjects(true);

      // scene.background = background;

      // 2. Downsample to Half resolution
      fsQuad.material = copyMaterial;
      copyMaterial.uniforms.tDiffuse.value = maskRenderTarget.texture;
      renderer.setRenderTarget(maskDownsampleRenderTarget);
      fsQuad.render(renderer);

      // 3. Apply Edge Detection THREE.Pass
      fsQuad.material = edgeDetectionMaterial;
      edgeDetectionMaterial.uniforms.maskTexture.value = maskDownsampleRenderTarget.texture;
      edgeDetectionMaterial.uniforms.visibleEdgeColor.value = visibleEdgeColor;
      // edgeDetectionMaterial.uniforms.hiddenEdgeColor.value = hiddenEdgeColor;
      renderer.setRenderTarget(edgeRenderTarget);
      fsQuad.render(renderer);

      // 4. Apply Blur on Half res
      fsQuad.material = separableBlurMaterial1;
      separableBlurMaterial1.uniforms.colorTexture.value = edgeRenderTarget.texture;
      separableBlurMaterial1.uniforms.kernelRadius.value = EDGE_THICKNESS;
      separableBlurMaterial1.uniforms.direction.value = OutlineSelectionPass.blurDirectionX;
      renderer.setRenderTarget(blur1stRenderTarget);
      fsQuad.render(renderer);
      2;
      separableBlurMaterial1.uniforms.colorTexture.value = blur1stRenderTarget.texture;
      separableBlurMaterial1.uniforms.direction.value = OutlineSelectionPass.blurDirectionY;
      renderer.setRenderTarget(edgeRenderTarget);
      fsQuad.render(renderer);

      // Blend it additively over the input texture
      fsQuad.material = overlayMaterial;
      overlayMaterial.uniforms.baseTexture.value = readBuffer.texture;
      overlayMaterial.uniforms.edgeTexture.value = edgeRenderTarget.texture;
      overlayMaterial.uniforms.edgeStrength.value = EDGE_STRENGTH;

      if (renderToScreen) {
        renderer.setRenderTarget(null);
      } else {
        renderer.setRenderTarget(writeBuffer);
      }

      fsQuad.render(renderer);

      renderer.setClearColor(clearColor, clearAlpha);
      renderer.autoClear = autoClear;
    } else {
      // Pass-through.
      fsQuad.material = copyMaterial;
      copyMaterial.uniforms.tDiffuse.value = readBuffer.texture;

      if (renderToScreen) {
        renderer.setRenderTarget(null);
      } else {
        renderer.setRenderTarget(writeBuffer);
      }

      fsQuad.render(renderer);
    }
  }
}

const DOWNSAMPLE_RATIO = 2;
const EDGE_STRENGTH = 1.0;
const EDGE_THICKNESS = 0.1;

function createCopyMaterial(): ShaderMaterial {
  return new ShaderMaterial({
    uniforms: {
      ...UniformsUtils.clone(CopyShader.uniforms),
      opacity: { value: 1.0 },
    },
    vertexShader: CopyShader.vertexShader,
    fragmentShader: CopyShader.fragmentShader,
    blending: NoBlending,
    depthTest: false,
    depthWrite: false,
    transparent: true,
  });
}

function createEdgeDetectionMaterial(): ShaderMaterial {
  return new ShaderMaterial({
    uniforms: {
      maskTexture: { value: null },
      texSize: { value: new Vector2(0.5, 0.5) },
      visibleEdgeColor: { value: new Vector3(1.0, 1.0, 1.0) },
      // hiddenEdgeColor: { value: new Vector3(1.0, 1.0, 1.0) },
    },
    vertexShader: [
      'varying vec2 vUv;',
      'void main() {',
      '  vUv = uv;',
      '  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );',
      '}',
    ].join('\n'),
    fragmentShader: [
      'varying vec2 vUv;',
      'uniform sampler2D maskTexture;',
      'uniform vec2 texSize;',
      'uniform vec3 visibleEdgeColor;',
      // 'uniform vec3 hiddenEdgeColor;',
      'void main() {',
      '  vec2 invSize = 1.0 / texSize;',
      '  vec4 uvOffset = vec4(1.0, 0.0, 0.0, 1.0) * vec4(invSize, invSize);',
      '  vec4 c1 = texture2D( maskTexture, vUv + uvOffset.xy);',
      '  vec4 c2 = texture2D( maskTexture, vUv - uvOffset.xy);',
      '  vec4 c3 = texture2D( maskTexture, vUv + uvOffset.zw);',
      '  vec4 c4 = texture2D( maskTexture, vUv - uvOffset.zw);',
      '  float diff1 = (c1.r - c2.r)*0.5;',
      '  float diff2 = (c3.r - c4.r)*0.5;',
      '  float d = length( vec2(diff1, diff2) );',
      // '  float a1 = min(c1.g, c2.g);',
      // '  float a2 = min(c3.g, c4.g);',
      // '  float visibilityFactor = min(a1, a2);',
      // '  vec3 edgeColor = 1.0 - visibilityFactor > 0.001 ? visibleEdgeColor : hiddenEdgeColor;',
      // '  gl_FragColor = vec4(edgeColor, 1.0) * vec4(d);',
      '  gl_FragColor = vec4(visibleEdgeColor, 1.0) * vec4(d);',
      '}',
    ].join('\n'),
    blending: NoBlending,
    depthTest: false,
    depthWrite: false,
  });
}

function createPrepareMaskMaterial(): ShaderMaterial {
  return new ShaderMaterial({
    uniforms: {
      // depthTexture: { value: null },
      cameraNearFar: { value: new Vector2(0.5, 0.5) },
      textureMatrix: { value: new Matrix4() },
    },
    vertexShader: [
      'varying vec4 projTexCoord;',
      'varying vec4 vPosition;',
      'uniform mat4 textureMatrix;',
      'void main() {',
      '  vPosition = modelViewMatrix * vec4( position, 1.0 );',
      '  vec4 worldPosition = modelMatrix * vec4( position, 1.0 );',
      '  projTexCoord = textureMatrix * worldPosition;',
      '  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );',
      '}',
    ].join('\n'),
    fragmentShader: [
      '#include <packing>',
      'varying vec4 vPosition;',
      'varying vec4 projTexCoord;',
      // 'uniform sampler2D depthTexture;',
      'uniform vec2 cameraNearFar;',
      'void main() {',
      // '  float depth = unpackRGBAToDepth(texture21DProj( depthTexture, projTexCoord ));',
      // '  float viewZ = -perspectiveDepthToViewZ( depth, cameraNearFar.x, cameraNearFar.y );',
      // '  float depthTest = (-vPosition.z > viewZ) ? 1.0 : 0.0;',
      // '  gl_FragColor = vec4(0.0, depthTest, 1.0, 1.0);',
      '  gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);',
      '}',
    ].join('\n'),
    side: DoubleSide,
  });
}

function createSeperableBlurMaterial(maxRadius: number): ShaderMaterial {
  return new ShaderMaterial({
    defines: {
      MAX_RADIUS: maxRadius,
    },
    uniforms: {
      colorTexture: { value: null },
      texSize: { value: new Vector2(0.5, 0.5) },
      direction: { value: new Vector2(0.5, 0.5) },
      kernelRadius: { value: 1.0 },
    },
    vertexShader: [
      'varying vec2 vUv;',
      'void main() {',
      '  vUv = uv;',
      '  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );',
      '}',
    ].join('\n'),
    fragmentShader: [
      '#include <common>',
      'varying vec2 vUv;',
      'uniform sampler2D colorTexture;',
      'uniform vec2 texSize;',
      'uniform vec2 direction;',
      'uniform float kernelRadius;',
      'float gaussianPdf(in float x, in float sigma) {',
      '  return 0.39894 * exp( -0.5 * x * x/( sigma * sigma))/sigma;',
      '}',
      'void main() {',
      '  vec2 invSize = 1.0 / texSize;',
      '  float weightSum = gaussianPdf(0.0, kernelRadius);',
      '  vec4 diffuseSum = texture2D( colorTexture, vUv) * weightSum;',
      '  vec2 delta = direction * invSize * kernelRadius/float(MAX_RADIUS);',
      '  vec2 uvOffset = delta;',
      '  for( int i = 1; i <= MAX_RADIUS; i ++ ) {',
      '    float w = gaussianPdf(uvOffset.x, kernelRadius);',
      '    vec4 sample1 = texture2D( colorTexture, vUv + uvOffset);',
      '    vec4 sample2 = texture2D( colorTexture, vUv - uvOffset);',
      '    diffuseSum += ((sample1 + sample2) * w);',
      '    weightSum += (2.0 * w);',
      '    uvOffset += delta;',
      '  }',
      '  gl_FragColor = diffuseSum/weightSum;',
      '}',
    ].join('\n'),
  });
}

function createOverlayMaterial(): ShaderMaterial {
  return new ShaderMaterial({
    uniforms: {
      baseTexture: { value: null },
      edgeStrength: { value: 1.0 },
      edgeTexture: { value: null },
    },
    vertexShader: [
      'varying vec2 vUv;',
      'void main() {',
      '  vUv = uv;',
      '  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );',
      '}',
    ].join('\n'),
    fragmentShader: [
      'varying vec2 vUv;',
      'uniform sampler2D baseTexture;',
      'uniform sampler2D edgeTexture;',
      'uniform float edgeStrength;',
      'void main() {',
      '  vec4 edgeValue = texture2D(edgeTexture, vUv);',
      '  vec4 baseValue = texture2D(baseTexture, vUv);',
      // NOTE: Emulate hardware Additive-blending
      '  gl_FragColor = baseValue + edgeValue;',
      '}',
    ].join('\n'),
    // NOTE: Hardware Additive-blending is creating artifacts with the current setup.
    // blending: AdditiveBlending,
    blending: NoBlending,
    depthTest: false,
    depthWrite: false,
  });
}
